<template>
  <main-section
    v-if="metaInfo"
    :meta-info="metaInfo"
    section-id=""
    title=""
    :sections="sections"
    image-url=""
  ></main-section>
  <div id="agency-page" v-if="regionData">
    <div class="agency">
      <structured-json-seo :region-data="regionData" />
      <header class="agency__hero">
        <div class="agency-title">
          <h1 class="agency-city" ref="agencyCityHeading">{{ getCityName }}</h1>
        </div>
        <div class="office-actions">
          <form-button
            text="Tilaa arviokäynti"
            theme="primary"
            @click.native="openAgencyForm()"
            tabindex="0"
          ></form-button>
        </div>
      </header>

      <call-to-action :actions="callToActionHeaderContent"></call-to-action>
      <div class="main-section">
        <div class="section-wrapper opds-color-background-default">
          <three-cards :content="threeCardsContent" customClass="list-blocks"></three-cards>
        </div>
      </div>

      <div id="agency-news-container" v-if="news">
        <div v-if="news.length > 0">
          <div id="agency-news" v-for="newsObj in news" :key="newsObj.name">
            <h2 class="company-title">{{ newsObj.name }}</h2>
            <div v-if="newsObj.news.length > 0" v-for="newsItem in newsObj.news" :key="`news-item-${newsItem.id}`">
              <div class="agency-news-container">
                <p class="title">{{ newsItem.title }}</p>
                <p class="content">{{ newsItem.body }}</p>
                <div class="action-button-container">
                  <template v-if="newsItem.links.length > 0" v-for="link in newsItem.links">
                    <a
                      :href="link.url"
                      class="button"
                      :target="
                        link.url.startsWith('#') || link.url.startsWith('https://op-koti.fi') ? '_self' : '_blank'
                      "
                    >
                      {{ link.title }}
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="agent__details" v-if="uniqueAgents?.length">
        <div class="inner-wrapper">
          <h2>Sinua palvelevat kiinteistönvälittäjät</h2>
          <p class="agent__ingress">
            Kun ostat tai myyt asuntoa, saat meiltä asiantuntijan avuksesi. Olemme mukanasi koko asunnonvaihdon ajan. OP
            Kodin vahvan osaamisen, paikallistuntemuksen ja uusimpien digitaalisten palveluiden avulla onnistut.
          </p>
          <div class="agent__contacts">
            <template v-for="person in uniqueAgents">
              <person
                :key="`person-${person.agentId}`"
                v-if="!person.hidden"
                :personnelLink="true"
                :person="person"
                :pictures-exist="picturesExist()"
                :are-images-loaded="areImagesLoaded"
              ></person>
            </template>
          </div>
        </div>
      </div>

      <div id="agency-companies" v-if="uniqueCompanies?.length">
        <companies :companies="uniqueCompanies" :title="getCompanyTitle"></companies>
      </div>

      <div id="agency-offices" v-if="uniqueOfficeCities?.length">
        <offices
          :offices="uniqueOfficeCities"
          title="Toimistojen yhteystiedot"
          :personnelLinks="true"
          :areBusinessAreaOffices="true"
        >
        </offices>
      </div>
      <div>
        <introduction-block :block-data="introductionBlockData">
          <template #actions>
            <img class="agency-intro-image" :src="getImageSrc()" />
          </template>
        </introduction-block>
      </div>
      <div class="line-divider" v-if="listings?.length"></div>
      <section class="agency__listings" v-if="listings?.length">
        <div class="agency__listings__wrapper">
          <suggested-listings :title="`Uusimmat myytävät asunnot - ${companyName}`" :suggestions="listings">
            <template v-slot:footnote>
              <div>
                <router-link :to="{ path: apartments, query: { companyId: companyId } }" class="basic-link"
                  >Näytä kaikki välittämämme asunnot &gt;</router-link
                >
              </div>
            </template>
          </suggested-listings>
        </div>
      </section>

      <call-to-action :actions="callToActionContent"></call-to-action>

      <top-agencies></top-agencies>
    </div>
  </div>
  <error-message v-else-if="error"></error-message>
  <dialogue identifier="agency-dialogue">
    <template #dialogueContent>
      <agency-contact :region-name="region?.name" :office="office" :person="person"></agency-contact>
    </template>
  </dialogue>
</template>
<script lang="ts">
import * as actions from '@/app/store/action-types';
import MainSection from '@/app/components/main-section/main-section.vue';
import { search } from '@/app/routes';
import config from '@/config';
import Person from '@/app/components/offices/person.vue';
import Offices from '@/app/components/offices/offices.vue';
import Companies from '@/app/components/companies/companies.vue';
import StructuredJsonSeo from '@/app/components/structured-json-seo/structured-json-seo.vue';
import VueScrollTo from 'vue-scrollto';
import { computed, defineAsyncComponent, defineComponent, ref, watch } from 'vue';
import Spinner from '@/app/components/spinner/spinner.vue';
import CallToAction from '@/app/components/main-section/call-to-action.vue';
import cities from '@/utils/cities';
import MetaTags from '@/app/components/MetaTags.vue';
import ErrorMessage from '@/app/components/error-message/error-message.vue';
import useRegionData from '@/app/data/useRegionData';
import { useListingsData } from '@/app/data/useListingsData';
import { getAgencyNewsQueryKey } from '@/app/data/useAgencyNewsData';
import { agencyNewsQuery_Client } from '@/app/data/agencyNews.client';
import { useQueryClient } from '@tanstack/vue-query';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import isSSR from '@/utils/isSSR';
import type { CtaLinkType } from '@/types/schemaTypes';
import { useFocusWithWatch } from '@/app/composables/useFocusWithWatch';

export default defineComponent({
  name: 'agency',
  setup() {
    const route = useSafeRoute();
    const regionId = computed(() => {
      const id = getRouteParamAsString(route.value.params.id);
      return id || '';
    });
    const { data: regionData, error } = useRegionData(regionId);
    const news = ref([] as any[]);

    const uniqueAgents = computed(() => {
      if (!regionData.value) {
        return undefined;
      }
      const unique = [...regionData.value.agents];
      // Rendomize the agents
      for (let i = unique.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = unique[i];
        unique[i] = unique[j];
        unique[j] = temp;
      }
      return unique;
    });

    const uniqueOfficeCities = computed(() => regionData.value?.offices);

    const uniqueCompanies = computed(() => regionData.value?.companies);

    const region = computed(() => regionData.value?.companyRegion);

    const companyName = computed(() => {
      if (region.value?.businessInfo?.companyName) {
        return region.value.businessInfo.companyName;
      } else if (uniqueCompanies.value && uniqueCompanies.value.length > 0) {
        return uniqueCompanies.value[0].name;
      } else {
        return '';
      }
    });

    const companyId = computed(() => {
      if (region.value?.businessInfo?.companyId) {
        return region.value.businessInfo.companyId;
      } else if (uniqueCompanies.value && uniqueCompanies.value.length > 0) {
        return uniqueCompanies.value[0].id;
      } else {
        return undefined;
      }
    });

    // Get agency listings
    const filters = computed(() => ({
      companyId: companyId.value,
      featureGroup: 'apartment',
      limit: 4,
    }));
    const listingsEnabled = computed(() => companyId.value !== undefined);
    const { data: listingsData } = useListingsData(filters, listingsEnabled);
    const listings = computed(() => listingsData?.value?.listings);

    // Get news for each agency on the client side
    const client = useQueryClient();
    if (!isSSR) {
      watch(
        uniqueCompanies,
        async (companies) => {
          if (companies) {
            for (const company of companies) {
              // using for of loop to ensure that the news are fetched in sequence to avoid overloading server
              const data = await client.fetchQuery({
                queryKey: getAgencyNewsQueryKey(company.id),
                queryFn: () => agencyNewsQuery_Client(company.id),
              });
              if (data?.length > 0) {
                const companyExists = news.value.some(function (x: any) {
                  return x.name === company.name;
                });
                if (!companyExists) news.value.push({ name: company.name, news: data });
              }
            }
          }
        },
        { immediate: true, deep: true }
      );
    }

    const agencyCityHeading = ref<HTMLElement | null>(null);

    useFocusWithWatch(regionData, agencyCityHeading);

    return {
      regionData,
      error,
      listings,
      uniqueAgents,
      uniqueOfficeCities,
      uniqueCompanies,
      region,
      companyName,
      companyId,
      news,
      route,
      agencyCityHeading,
    };
  },
  data: () => ({
    person: null as any,
    office: null as any,
    cityName: null,
    loading: true,
    areImagesLoaded: false,
    threeCardsContent: {
      cardType: 'boxed',
      title: 'Ylivertainen kokonaisuus asuntosi myyntiin',
      sections: [
        {
          text: 'Laadukkaat kuvat ja videot sekä aktiivinen myynti ja markkinointi.',
          link: { text: 'Asunnon hinnoittelu', route: '/asunnon-markkinointi' },
        },
        {
          text: 'Seuraa mm. myynti-ilmoituksesi kävijämääriä ja kommentteja esittelyistä.',
          link: { text: 'Asunnon valmistelu myyntikuntoon', route: '/myyjan-palvelu' },
        },
        {
          text: 'Testaa hinta-arviolaskurilla asuntosi arvo - tarkemman arvion saat välittäjältä.',
          link: { text: 'Tehokas markkinointi', route: '/asunnon-hintalaskuri' },
        },
        {
          text: 'Asunnonvaihdon voit halutessasi hoitaa täysin digitaalisesti.',
          link: { text: 'Luotettava asuntokauppa', route: '/digitaalinen-asuntokauppa' },
        },
      ],
    },
    callToActionHeaderContent: [
      {
        type: 'agent-contact-information',
        name: 'Välittäjien yhteystiedot',
        iconClass: 'icon-person-circled',
      },
      {
        type: 'agency-contact-information',
        name: 'Toimistojen yhteystiedot',
        iconClass: 'icon-home-circled',
      },
      {
        type: 'link',
        name: 'Testaa asuntosi arvo',
        iconClass: 'icon-loans-circled',
        route: '/asunnon-hintalaskuri',
      },
    ] as CtaLinkType[],
    callToActionContent: [
      {
        type: 'link',
        name: 'Myytävät asunnot',
        route: '/myytavat/asunnot',
        iconClass: 'icon-search-circled',
      },
      {
        type: 'contact-agency',
        name: 'Ota yhteyttä',
        iconClass: 'icon-speech-bubble-circled',
      },
    ] as CtaLinkType[],
    formAnalytics: {
      eVar21: 'kiinteistovalitys',
      eVar22: 'viesti-valittajalle',
    },
  }),

  methods: {
    scrollToAgentInformation() {
      const element = document.querySelector('.agent__details');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    scrollToAgencyInformation() {
      const element = document.querySelector('.offices__title');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    replaceScandinavianLetters(str: string) {
      return str.replace(/[åä]/gi, 'a').replace(/[ö]/gi, 'o').replace(/[ÅÄ]/g, 'A').replace(/[Ö]/g, 'O');
    },
    handleScroll() {
      if (this.areImagesLoaded) return false;
      this.areImagesLoaded = true;
    },
    picturesExist() {
      return this.uniqueAgents ? Object.values(this.uniqueAgents).some((x) => 'pictureUrl' in x) : false;
    },
    formAgencyDescription() {
      const city = this.formattedRouteCityName;
      return `Luotettavaa ja asiantuntevaa kiinteistönvälitystä paikkakunnalla ${city}. Ota yhteyttä – tehdään arvio sekä myyntisuunnitelma asunnostasi.`;
    },
    getImageSrc() {
      return this.region?.businessInfo?.pictureUrl
        ? this.region?.businessInfo?.pictureUrl
        : 'https://op-koti.fi/assets/op-koti-sign.png';
    },
    openAgencyForm() {
      this.person = null;
      this.$bus.$emit('openDialogue', 'agency-dialogue');
    },
    sendPageAnalytics() {
      const agencyName = this.$filters.capitalizeFirstLetter(this.$route.params.id);
      void this.$store.dispatch(actions.analytics.set, {
        pageName: `kiinteistonvalitys:${this.$route.params.id}`,
        agencyName: agencyName,
        eVar17: this.metaInfo.title,
      });
      void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
    },
    scrollToTop() {
      const duration = 400;
      const options = {
        easing: 'ease-out',
        offset: -50,
        cancelable: true,
      };
      VueScrollTo.scrollTo(document.getElementById('vue-app'), duration, options);
    },
    openContactFormIfNecessary() {
      const contact = this.route.query.contact?.toString() || '';
      if (contact.toLowerCase() === 'true') {
        this.openAgencyForm();
      }
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);

    this.sendPageAnalytics();

    this.$bus.$on(
      'openPersonContactDialogue',
      (event) => {
        this.person = event.person;
        this.office = event.office;
        this.$bus.$emit('openDialogue', 'agency-dialogue');
      },
      this
    );

    this.$bus.$on(
      'dialogueClosed',
      () => {
        this.person = null;
        this.office = null;
      },
      this
    );

    this.$bus.$on(
      'scrollToAgentInformation',
      () => {
        this.scrollToAgentInformation();
      },
      this
    );

    this.$bus.$on(
      'scrollToAgencyInformation',
      () => {
        this.scrollToAgencyInformation();
      },
      this
    );

    this.openContactFormIfNecessary();
  },
  computed: {
    metaInfo() {
      return {
        title: `Kiinteistönvälitys ${this.formattedRouteCityName} - OP Koti` || 'Toimipistettä ei löytynyt - OP Koti',
        meta: [{ vmid: 'description', name: 'description', content: this.formAgencyDescription() }],
      };
    },
    getCompanyTitle() {
      return this.uniqueCompanies?.length === 1 ? 'Yrityksen yhteystiedot' : 'Yritysten yhteystiedot';
    },
    getCityName() {
      return !!this.region && this.region.hasOwnProperty('name')
        ? `Kiinteistönvälitys ${this.region.name}`
        : 'Kiinteistönvälitys ' + this.formattedRouteCityName;
    },
    formattedRouteCityName() {
      const id = getRouteParamAsString(this.route.params.id) || '';
      const cityId = this.replaceScandinavianLetters(id);

      const city = cities.find(
        (city) => this.replaceScandinavianLetters(city.name.toLowerCase()) === cityId.toLowerCase()
      );

      return !!city ? this.$filters.capitalizeFirstLetter(city.name) : '';
    },
    sections() {
      return [];
    },
    apartments() {
      return search.sale.routes.find((route) => route.id === config.testing.NAVIGATION.BUYING.APARTMENTS)?.path || '';
    },
    introductionBlockData() {
      // @ts-ignore
      const city = this.region ? this.$filters.capitalizeFirstLetter(this.region.name) : this.formattedRouteCityName;
      return {
        title: `Kiinteistövälitys ${city}`,
        paragraphs:
          this.region &&
          this.region.businessInfo &&
          this.region.businessInfo.description &&
          this.region.businessInfo.description.length > 0
            ? this.region.businessInfo.description
            : [
                'Onko asunnon ostaminen tai myyminen ajankohtaista ja etsit osaavaa kiinteistönvälittäjää? OP Koti palvelee sinua kaikissa kiinteistönvälitykseen liittyvissä asioissa.',
                `Kiinteistönvälittäjämme ovat tehneet lukuisia onnistuneita asuntokauppoja alueella. Paikallinen kiinteistönvälittäjä tuntee tämän seudun asunnot, markkinatilanteen, kysynnän ja hintatason. Tämä on olennaista, sillä asuntokaupat ja kiinteistönvälitys ${city} vaativat vankkaa ammattitaitoa.`,
                'OP Kodissa kotisi on luotettavan ja asiantuntevan kiinteistönvälittäjän käsissä. Laadimme sinulle aina räätälöidyn myyntisuunnitelman ja aikataulun. Pidämme sinuun säännöllisesti yhteyttä, jotta pysyt ajan tasalla. Digitaalisen myyjän palvelumme avulla voit seurata reaaliajassa asuntosi myynnin etenemistä.',
                'Ota yhteyttä - toteutetaan yhdessä sinun asumisen unelmasi ja suunnitellaan juuri sinulle sopiva palvelukokonaisuus. Yhteydenotto kiinteistönvälittäjään ei sido sinua mihinkään. Osuuspankin omistaja-asiakkaana OP-bonuksiasi käytetään OP Kodin kiinteistönvälityspalkkion maksamiseen.',
                `Olit sitten myymässä tai ostamassa uutta, jätä kiinteistönvälitys huoleksemme ja keskity unelmiesi toteuttamiseen – kiinteistönvälitys ${city} palveluksessasi!`,
              ],
        bgColor: 'white',
      };
    },
    anchorLinks() {
      return [
        {
          scrollToId: 'agency-introduction',
          iconClass: 'icon-info-circled',
          label: 'Tietoa meistä',
        },
        {
          scrollToId: 'agency-offices',
          iconClass: 'icon-bankoffice-circled',
          label: 'Toimipisteet',
        },
      ];
    },
  },
  beforeUnmount() {
    this.$bus.$off('openPersonContactDialogue', this);
    this.$bus.$off('scrollToAgentInformation', this);
    this.$bus.$off('scrollToAgencyInformation', this);
    this.$bus.$off('dialogueClosed', this);
    if (!isSSR) {
      window.removeEventListener('scroll', this.handleScroll);
    }
  },
  components: {
    MainSection,
    StructuredJsonSeo,
    CallToAction,
    Dialogue: defineAsyncComponent(() => import('@/app/components/dialogue/dialogue.vue')),
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    AgencyContact: defineAsyncComponent(() => import('@/app/components/agency-contact/agency-contact.vue')),
    IntroductionBlock: defineAsyncComponent(() => import('@/app/components/introduction-block/introduction-block.vue')),
    SingleCard: defineAsyncComponent(() => import('@/app/components/single-card/single-card.vue')),
    Person,
    Offices,
    Companies,
    TopAgencies: defineAsyncComponent(() => import('@/app/components/top-agencies/top-agencies.vue')),
    SuggestedListings: defineAsyncComponent(() => import('@/app/components/suggested-listings/suggested-listings.vue')),
    Spinner,
    MetaTags,
    ErrorMessage,
    ThreeCards: defineAsyncComponent(() => import('@/app/components/three-cards/three-cards.vue')),
  },
});
</script>
